import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { Department } from '@app/features/scoping/models/department.model'
import { Role } from '@app/features/scoping/models/role.model'
import { plainToInstance } from 'class-transformer'
import { ScopeSection } from '../model/scope-section'
import { getMoneyValue, mapScopeTpc } from './map-scope-entity.utils'
import { mapScopeFee } from './map-scope-fee.utils'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { Money } from '../model/money.model'

export const mapScopeDeliverable = (deliverable, sectionId?: number): Deliverable => {
  const section = mapDeliverableSection(deliverable, sectionId)
  const budgetAmount = typeof deliverable.budget === 'number' ? deliverable.budget : deliverable.budget?.amount
  const budgetBalanceAmount =
    typeof deliverable.budgetBalance === 'number' ? deliverable.budgetBalance : deliverable.budgetBalance?.amount
  const fixedCostOffsetAmount =
    typeof deliverable.fixedCostOffset === 'number' ? deliverable.fixedCostOffset : deliverable.fixedCostOffset?.amount

  return plainToInstance(Deliverable, {
    ...deliverable,
    section,
    budget: new Money(budgetAmount, deliverable.currencyUnit),
    budgetBalance: new Money(budgetBalanceAmount, deliverable.currencyUnit),
    fixedCostOffset: new Money(fixedCostOffsetAmount, deliverable.currencyUnit),
    feeItems: (deliverable.feeItems || []).map((feeItem) =>
      mapScopeFee(feeItem, { scopeSection: section, deliverable })
    ),
    thirdPartyCosts: (deliverable.thirdPartyCosts || []).map((tpc) => mapScopeTpc(tpc, deliverable.currencyUnit)),
    components: (deliverable.components || []).map((component) => mapDeliverableComponent(component, deliverable)),
    sections: (deliverable.sections || []).map((section) => mapSection(section, deliverable.currencyUnit)),
  })
}

export function mapSection(section: ScopeSection, currency: string) {
  return plainToInstance(ScopeSection, {
    ...section,
    thirdPartyCosts: (section?.thirdPartyCosts || []).map((tpc: ThirdPartyCost) => mapScopeTpc(tpc, currency)),
  })
}

export function mapDeliverableSection(deliverable: Deliverable, sectionId?: number) {
  if (deliverable.section) return mapSection(deliverable.section, deliverable.currencyUnit)

  return sectionId ? plainToInstance(ScopeSection, { id: sectionId }) : null
}

export function mapDeliverableComponent(component, deliverable: Deliverable) {
  return plainToInstance(ScopeComponent, {
    ...component,
    feeItems: (component.feeItems || []).map((feeItem) =>
      mapScopeFee(feeItem, { deliverable, component, scopeSection: deliverable.section })
    ),
    deliverable: plainToInstance(Deliverable, { id: deliverable.id }),
    departments: (component.departments || []).map((department) =>
      mapDeliverableDepartment(department, deliverable.currencyUnit)
    ),
    thirdPartyCosts: (component.thirdPartyCosts || []).map((tpc) => mapScopeTpc(tpc, deliverable.currencyUnit)),
  })
}

export function mapDeliverableDepartment(department, currency: string) {
  return plainToInstance(Department, {
    ...department,
    roles: department.roles.map((role) => mapDeliverableRole(role, currency)),
  })
}

export function mapDeliverableRole(role, currency: string) {
  return plainToInstance(Role, {
    ...role,
    rateCardRate: getMoneyValue(role.rate, currency),
    rateCardCost: getMoneyValue(role.cost, currency),
    rateCardMinutes: role.rateCardHours ? role.rateCardHours * 60 : 0,
  })
}

/** to API utils */

export function mapScopeDeliverableToPlainApi(deliverable: Deliverable) {
  if (!deliverable) return null

  return plainToInstance(Deliverable, {
    ...deliverable,
    components: null,
  })
}
